import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowRight, FaRocket, FaBrain, FaChartLine, FaCheck, FaUsers, FaAward, FaTimes, FaPhone } from 'react-icons/fa';
import HeroSlider from '../components/HeroSlider';
import toast from 'react-hot-toast';
import { PopupButton } from 'react-calendly';

const features = [
  {
    icon: <FaRocket className="text-4xl mb-4" />,
    title: 'Fast Implementation',
    description: 'Get your AI solutions up and running quickly with our streamlined process.'
  },
  {
    icon: <FaBrain className="text-4xl mb-4" />,
    title: 'Smart Solutions',
    description: 'Leverage cutting-edge AI technology to solve complex business challenges.'
  },
  {
    icon: <FaChartLine className="text-4xl mb-4" />,
    title: 'Scalable Growth',
    description: 'Solutions that grow with your business, from startup to enterprise.'
  }
];

const stats = [
  { number: '98%', label: 'Client Satisfaction' },
  { number: '250+', label: 'Projects Completed' },
  { number: '50+', label: 'AI Experts' },
  { number: '24/7', label: 'Support' }
];

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'CEO, TechVision Global',
    content: 'Botrex AI has revolutionized our business operations. Their AI solutions have not only streamlined our processes but have given us insights we never thought possible. The ROI has been exceptional.',
    image: '/images/testimonials/client1.jpg'
  },
  {
    name: 'James Wilson',
    role: 'CEO, TechVision Inc',
    content: 'The AI chatbot developed by Botrex has transformed our customer service. Response times are down 80%, and customer satisfaction is up by 60%. Truly revolutionary!',
    image: '/images/testimonials/client2.jpg'
  },
  {
    name: 'Marcus Johnson',
    role: 'Director of AI, DataFlow Systems',
    content: 'Botrex\'s AI integration capabilities are unmatched. They helped us implement a sophisticated data analysis system that has given us incredible insights into our operations.',
    image: '/images/testimonials/client3.jpg'
  }
];

const services = [
  {
    icon: <FaRocket className="text-4xl mb-4" />,
    title: 'AutoWave Pro',
    description: 'Our flagship AI agent platform featuring web browsing, code editing, document creation, SEO optimization, and advanced analytics.',
    isProduct: true,
    link: 'https://www.autowave.pro'
  },
  {
    icon: <FaUsers className="text-4xl mb-4" />,
    title: 'AI Consulting',
    description: 'Strategic AI implementation, process optimization, technology assessment, and ROI analysis.'
  },
  {
    icon: <FaAward className="text-4xl mb-4" />,
    title: 'Custom AI Solutions',
    description: 'Machine learning models, natural language processing, computer vision, and predictive analytics.'
  },
  {
    icon: <FaBrain className="text-4xl mb-4" />,
    title: 'AI Training',
    description: 'AI training and education for businesses, including workshops and online courses.'
  }
];

const Home = () => {
  const navigate = useNavigate();
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success(isSignUp ? 'Account created successfully!' : 'Signed in successfully!');
    setShowAuthForm(false);
    navigate('/packages');
  };

  return (
    <div className="bg-nvidia-dark">
      {/* Hero Section */}
      <section className="relative bg-nvidia-dark-800 py-20">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white from-40% via-nvidia-green via-70% to-green-400">
              Transform Your Business with AI
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Leverage cutting-edge artificial intelligence solutions to drive innovation and growth
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <PopupButton
                url="https://calendly.com/rahwsty/30min"
                rootElement={document.getElementById('root')}
                text="Schedule a Consultation"
                className="bg-nvidia-green text-black px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors flex items-center justify-center"
              />
              <Link
                to="/services"
                className="border border-nvidia-green text-nvidia-green px-8 py-3 rounded-lg font-semibold hover:bg-nvidia-green hover:text-black transition-colors flex items-center justify-center"
              >
                Our Services
                <FaArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-nvidia-dark">Why Choose Us</h2>
            <p className="text-xl text-nvidia-gray-600 mb-8">
              We deliver cutting-edge AI solutions tailored to your needs
            </p>
            <div className="max-w-5xl mx-auto mb-12 relative group">
              <img
                src="/images/why-choose-us/main-image.png"
                alt="Why Choose Botrex AI"
                className="w-full h-auto rounded-lg shadow-xl"
                style={{
                  imageRendering: 'high-quality',
                  WebkitFontSmoothing: 'antialiased',
                  objectFit: 'contain',
                  filter: 'contrast(1.08) saturate(1.08) brightness(1.02) sharpen(1.5)',
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden'
                }}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-6 rounded-lg bg-nvidia-dark-800 transform hover:-translate-y-1 transition-transform duration-300 border border-nvidia-dark-600"
              >
                <div className="text-nvidia-green">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                <p className="text-nvidia-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-nvidia-dark to-nvidia-dark-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-xl text-gray-400 mb-6">Transforming businesses with cutting-edge AI solutions</p>
            <div className="max-w-3xl mx-auto">
              <p className="text-gray-300 leading-relaxed">
                At Botrex AI, we don't just implement technology – we craft intelligent solutions that understand and adapt to your unique business needs. Our expertise goes beyond conventional AI integration; we're your partners in navigating the future of business intelligence.
              </p>
              <p className="text-gray-300 leading-relaxed mt-4">
                Whether you're a startup looking to disrupt your industry or an enterprise seeking to optimize operations, our tailored AI solutions combine human insight with machine precision. We transform complex challenges into streamlined processes, enabling you to make data-driven decisions with confidence.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="p-6 rounded-lg bg-nvidia-dark-800 transform hover:-translate-y-1 transition-transform duration-300 border border-nvidia-dark-600"
              >
                <div className="w-12 h-12 bg-nvidia-dark-700 rounded-lg flex items-center justify-center mb-4 text-nvidia-green">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
                <p className="text-nvidia-gray-400 mb-4">{service.description}</p>
                {service.isProduct ? (
                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-nvidia-green text-black px-4 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
                  >
                    Try AutoWave Pro
                    <FaArrowRight className="ml-2" />
                  </a>
                ) : (
                  <Link
                    to={
                      service.title === 'AI Consulting'
                        ? '/ai-consulting'
                        : service.title === 'Custom AI Solutions'
                        ? '/custom-ai-solutions'
                        : '/services'
                    }
                    className="inline-flex items-center text-nvidia-green hover:text-nvidia-green/90 transition-colors"
                  >
                    Learn More
                    <FaArrowRight className="ml-2" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-nvidia-dark">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-nvidia-green mb-2">{stat.number}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Image Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="flex justify-center">
            <img
              src="/images/testimonials/testimonials-image.avif"
              alt="Client Testimonial"
              className="w-full md:w-auto md:h-[700px] h-auto rounded-lg object-cover shadow-lg md:shadow-xl transform transition-transform duration-300 md:[filter:contrast(110%)_brightness(105%)_saturate(105%)]"
              style={{ 
                imageRendering: 'crisp-edges',
                WebkitFontSmoothing: 'antialiased',
                filter: 'contrast(105%) brightness(102%)'
              }}
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-nvidia-dark-900">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">
            Client <span className="text-nvidia-green">Testimonials</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-nvidia-green"
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{testimonial.name}</h3>
                    <p className="text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "{testimonial.content}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action with Image Upload */}
      <section className="py-20 bg-nvidia-dark-900">
        <div className="container mx-auto px-6">
          {/* Image Upload Directories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Left Image */}
            <div className="bg-white/5 rounded-lg p-8 border border-nvidia-green/30 hover:border-nvidia-green transition-colors">
              <img
                src="/images/cta/ai pic 23.jpeg"
                alt="AI Processing Example"
                className="w-full h-[300px] object-cover rounded-lg mb-6"
              />
              <div className="text-center px-4">
                <h3 className="text-2xl font-bold text-nvidia-green mb-3">Intelligent Processing</h3>
                <p className="text-gray-300 leading-relaxed italic">
                  "Experience the future of AI processing where complex algorithms meet intuitive design. Our advanced systems analyze and optimize your data with unprecedented accuracy and efficiency."
                </p>
              </div>
            </div>

            {/* Right Image */}
            <div className="bg-white/5 rounded-lg p-8 border border-nvidia-green/30 hover:border-nvidia-green transition-colors">
              <img
                src="/images/cta/ai pic 19.png"
                alt="AI Processing Result"
                className="w-full h-[300px] object-cover rounded-lg mb-6"
              />
              <div className="text-center px-4">
                <h3 className="text-2xl font-bold text-nvidia-green mb-3">Results That Matter</h3>
                <p className="text-gray-300 leading-relaxed italic">
                  "Transform raw potential into measurable success. Our AI solutions deliver clear, actionable results that drive your business forward in the digital age."
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action Text */}
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how our AI solutions can help you achieve your business goals
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <PopupButton
                url="https://calendly.com/rahwsty/30min"
                rootElement={document.getElementById('root')}
                text="Schedule a Consultation"
                className="bg-nvidia-green text-black px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors flex items-center justify-center"
              />
              <Link
                to="/contact"
                className="border border-nvidia-green text-nvidia-green px-8 py-3 rounded-lg font-semibold hover:bg-nvidia-green hover:text-black transition-colors flex items-center justify-center"
              >
                Contact Us
                <FaPhone className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Insights Section */}
      <section className="py-20 bg-nvidia-dark">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Latest Insights</h2>
            <p className="text-xl text-gray-400 mb-8">
              Stay updated with the latest trends and developments in AI
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Add your blog post cards here */}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/blog"
              className="inline-flex items-center text-nvidia-green hover:text-nvidia-green/90 transition-colors"
            >
              View All Articles
              <FaArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Auth Form Modal */}
      {showAuthForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
            <button
              onClick={() => setShowAuthForm(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <FaTimes size={24} />
            </button>
            
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">
                {isSignUp ? 'Create Account' : 'Welcome Back'}
              </h2>
              <p className="text-gray-600 mt-2">
                {isSignUp ? 'Get started with Botrex AI' : 'Sign in to your account'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <div>
                  <label className="block text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-nvidia-green focus:ring-1 focus:ring-nvidia-green"
                    required
                  />
                </div>
              )}
              
              <div>
                <label className="block text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-nvidia-green focus:ring-1 focus:ring-nvidia-green"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-nvidia-green focus:ring-1 focus:ring-nvidia-green"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-nvidia-green text-black py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
              >
                {isSignUp ? 'Sign Up' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-nvidia-green hover:text-opacity-90"
              >
                {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
